import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Conversation } from "@/types/conversation/conversation";
import { ConversationSectionCard } from "./ConversationSectionCard";

interface CompactInsightsSectionProps {
  conversation: Conversation;
}

const renderValueContent = (value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "") {
    return <span className="text-gray-400">Not extracted</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">None</span>;
    return <JsonValueRenderer value={value} />;
  }
  if (typeof value === "object") {
    return <JsonValueRenderer value={value} />;
  }
  return String(value);
};

const JsonValueRenderer = ({ value }: { value: object }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">None</span>;
    return (
      <div className="ml-3 sm:ml-4 space-y-1">
        {value.map((item, idx) => (
          <div key={idx} className="text-xs sm:text-sm text-gray-700">
            • {typeof item === "object" ? JSON.stringify(item) : String(item)}
          </div>
        ))}
      </div>
    );
  }

  const entries = Object.entries(value);
  if (entries.length === 0) return <span className="text-gray-400">Empty object</span>;

  return (
    <div className="ml-3 sm:ml-4 mt-2 space-y-2 bg-gray-50 rounded p-2 border border-gray-200">
      {entries.map(([key, val]) => (
        <div key={key}>
          <div className="text-xs sm:text-sm text-gray-600 font-medium">{key}:</div>
          <div className="text-xs sm:text-sm text-gray-800 break-words ml-2">
            {typeof val === "object" ? JSON.stringify(val) : String(val || "-")}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * "Insights Captured" — the values the genie extracted on this call
 * (lead_info / goal fields), one row per field. Restored from the
 * pre-transcript-first layout; never returns null — when nothing is
 * configured or extracted it shows an empty state pointing at the
 * genie's insight settings, so the section is always visible.
 */
export const CompactInsightsSection = ({ conversation }: CompactInsightsSectionProps) => {
  const agentId = conversation?.agent_id;

  const leadInfo =
    conversation?.details?.lead_info || conversation?.details?.analysis_result?.lead_info || null;

  const entries = leadInfo && typeof leadInfo === "object" ? Object.entries(leadInfo) : [];
  const hasConfiguredFields =
    (conversation?.agent?.insights?.fields?.length ?? 0) > 0 ||
    (conversation?.agent?.goal?.fields?.length ?? 0) > 0;

  const getFieldLabel = (key: string): string => key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <ConversationSectionCard
      title="Insights Captured"
      genieIcon="clipboard-check"
      action={
        agentId && (
          <Link
            to={`/genie-hub/my-genies/${agentId}?section=section-insights`}
            className="inline-flex items-center gap-1 text-xs hover:gap-1.5 transition-all flex-shrink-0"
          >
            <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
              Edit insights
            </span>
            <ArrowRight className="w-3 h-3 text-[#FF4DB8]" />
          </Link>
        )
      }
    >
      {entries.length > 0 ? (
        <div className="space-y-3 sm:space-y-2">
          {entries.map(([key, value]) => (
            <div key={key} className="flex flex-col sm:grid sm:grid-cols-[minmax(200px,auto)_1fr] gap-1 sm:gap-3 py-1">
              <span className="text-xs sm:text-sm text-gray-500 font-medium">{getFieldLabel(key)}:</span>
              <div className="text-xs sm:text-sm text-gray-900 break-words">{renderValueContent(value)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs sm:text-sm text-gray-500">
          {hasConfiguredFields
            ? "No insights extracted on this call yet."
            : "No capture fields set up for this genie yet — add them so every call extracts the details you care about."}
        </div>
      )}
    </ConversationSectionCard>
  );
};
